import React, { useState } from 'react';
import { Checkbox } from 'ui';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { manageOptionsButton } from 'app/area/shared/manage-options';
import { SelectSingle, SelectSingleHierarchy } from 'app/area/shared/translated-components';

import { getFilterId } from '../utils/get-filter-id';
import { FilterCommonProps } from './filter-common';
import { FilterValidationWrapper } from './filter-validation-wrapper';

import styles from './filter-code.scss';

interface FilterMyValueProps {
  useMyValue: boolean;
  isHierarchy: boolean;
  myValueTranslation?: string;
}

const FilterCodeImplementation: React.FC<FilterCommonProps & FilterMyValueProps> = ({
  filter, metadata, isHierarchy, useMyValue, updateFilter,
  endpoints, options, loadOptions,
  validation, myValueTranslation, translation
}) => {

  const myValueId = getFilterId(filter.domain?.value, 'myValue');
  const codeId = getFilterId(filter.domain?.value, 'code');
  const categoryId = getFilterId(filter.domain?.value, 'category');

  const onChangeMyValue = (value: number) => {

    const { myValue: _, defaultValue: __, ...filteredValidation } = (validation ?? {});

    updateFilter(
      filter.domain?.value,
      {
        ...filter,
        myValue: value !== 0,
        defaultValue: undefined
      },
      filteredValidation
    );
  };

  const onChangeCode = (value: ComplexValue<number>) => {

    const { defaultValue: _, myValue: __, ...filteredValidation } = (validation ?? {});

    updateFilter(
      filter.domain?.value,
      {
        ...filter,
        defaultValue: value,
        myValue: undefined
      },
      filteredValidation
    );
  };

  const [codeLoadOptions, setCodeLoadOptions] = useState<LoadOptions>();

  const onSetCodeLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(codeId, endpoints.codeOptionsFor(filter.domain?.value), loadOptionsParams, { displayNotSelected: false });
    setCodeLoadOptions(loadOptionsParams);
  };

  const [categoryLoadOptions, setCategoryLoadOptions] = useState<LoadOptions>();

  const onSetCategoryLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(categoryId, endpoints.categoryOptionsFor(filter.domain?.value), loadOptionsParams);
    setCategoryLoadOptions(loadOptionsParams);
  };

  const selectCommonProps = {
    componentId: codeId,
    className: undefined,
    valueItem: filter.defaultValue?.value ? filter.defaultValue : undefined,
    onChange: onChangeCode,
    ...(!useMyValue && { validation: validation?.defaultValue }),

    options: options?.[codeId]?.codes as ComplexValue<number>[] || [],
    loadOptions: codeLoadOptions,
    onLoadOptions: onSetCodeLoadOptions,

    ...(isDefinedIncludingZero(metadata.categoryCodetable) && {
      displayCategories: true,
      categoryOptions: options?.[categoryId]?.codes as ComplexValue<number>[] || [],
      loadCategoryOptions: categoryLoadOptions,
      onLoadCategoryOptions: onSetCategoryLoadOptions
    }),

    fetching: options?.[codeId]?.fetching || options?.[categoryId]?.fetching || false,

    placeholder: translation?.search,

    searchRequired: metadata.searchRequired,

    extraFooterButton: manageOptionsButton(translation.manage_options, metadata.codetable)
  };

  return <FilterValidationWrapper showValidation={useMyValue} validationProp={validation?.defaultValue}>
    {!!useMyValue && <Checkbox
      className={styles.checkbox}
      componentId={myValueId}
      value={filter.myValue ? 1 : 0}
      label={{ text: myValueTranslation, visible: true }}
      onChange={onChangeMyValue}
    />}
    {isHierarchy
      ? <SelectSingleHierarchy {...selectCommonProps} descriptionAllLevels={true} />
      : <SelectSingle {...selectCommonProps} />}
  </FilterValidationWrapper>;
};

export const FilterFlatList: React.FC<FilterCommonProps> = (props) =>
  <FilterCodeImplementation {...props}
    isHierarchy={false}
    useMyValue={false} />;

export const FilterHierarchy: React.FC<FilterCommonProps> = (props) =>
  <FilterCodeImplementation {...props}
    isHierarchy={true}
    useMyValue={false} />;

export const FilterPerson: React.FC<FilterCommonProps> = (props) =>
  <FilterCodeImplementation
    {...props}
    isHierarchy={false}
    useMyValue={true}
    myValueTranslation={props.translation?.filters_person}
  />;

export const FilterLocation: React.FC<FilterCommonProps> = (props) =>
  <FilterCodeImplementation
    {...props}
    isHierarchy={true}
    useMyValue={true}
    myValueTranslation={props.translation?.filters_my_location}
  />;

export const FilterUnit: React.FC<FilterCommonProps> = (props) =>
  <FilterCodeImplementation
    {...props}
    isHierarchy={true}
    useMyValue={true}
    myValueTranslation={props.translation?.filters_my_unit}
  />;
